import { toMoney } from "@chatfood/core-utils";
import { Provider } from "../provider";
import type { Analytics } from "../analytics";
import type { ProviderListeners } from "../provider-listeners";
import type { ProviderEvent } from "../provider-event";
import type { IDomainMoney } from "#imports";

type IConfig = {
  containerId: string;
};

const moneyToFloat = (price: IDomainMoney) => {
  return parseFloat(
    toMoney(price.value, price.currency, {
      inUnitOutput: true,
    }),
  );
};

export class GoogleTagManager extends Provider {
  public key = "googleTagManager";
  protected analytics: Analytics | undefined;
  protected listeners: ProviderListeners = {
    userIdentify: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "identify_user",
        userId: payload.id,
      });
    },
    pageView: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "page_view",
        page: {
          url: payload.targetRoutePath,
          name: payload.targetRouteName,
        },
      });
    },
    viewMenu: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "view_menu",
        menu: {
          id: payload.id,
          slug: payload.slug,
        },
      });
    },
    viewItem: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "view_item",
        ecommerce: {
          detail: {
            products: [
              {
                id: payload.id,
                name: payload.name,
                price: moneyToFloat(payload.price),
                flow: payload.flow,
              },
            ],
          },
        },
      });
    },
    addItemToBasket: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "add_to_cart",
        ecommerce: {
          add: {
            products: [
              {
                id: payload.id,
                name: payload.name,
                price: moneyToFloat(payload.price),
                quantity: payload.quantity,
                total: moneyToFloat({
                  value: payload.price.value * payload.quantity,
                  currency: payload.price.currency,
                }),
                flow: payload.flow,
              },
            ],
          },
        },
      });
    },
    removeItemFromBasket: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "remove_from_cart",
        ecommerce: {
          remove: {
            products: [
              {
                id: payload.id,
                name: payload.name,
                price: moneyToFloat(payload.price),
                quantity: payload.quantity,
              },
            ],
          },
        },
      });
    },
    initiateCheckout: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "initiate_checkout",
        ecommerce: {
          checkout: {
            actionField: {
              coupon: payload.discountCode,
              total: moneyToFloat(payload.total),
            },
            products: payload.items.map((i) => ({
              id: i.id,
              name: i.name,
              quantity: i.quantity,
              price: moneyToFloat(i.price),
              total: moneyToFloat({
                value: i.price.value * i.quantity,
                currency: i.price.currency,
              }),
            })),
          },
        },
        currency: payload.total.currency,
      });
    },
    addPaymentInfo: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "add_payment_info",
        ecommerce: {
          checkout: {
            actionField: {
              option: payload.method,
            },
          },
        },
      });
    },
    selectTipAmount: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "select_tip_amount",
        ecommerce: {
          checkout: {
            actionField: {
              amount: moneyToFloat(payload.amount),
            },
          },
        },
      });
    },
    orderSuccess: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "order_success",
        ecommerce: {
          purchase: {
            actionField: {
              id: payload.orderUid,
            },
          },
        },
      });
    },
    orderFailure: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "order_failure",
        ecommerce: {
          purchase: {
            actionField: {
              id: payload.orderUid,
              reason: payload.reason ?? "Unknown",
            },
          },
        },
      });
    },
    placeOrder: (payload) => {
      return this.enhancePayloadAndTrack({
        event: "place_order",
        ecommerce: {
          purchase: {
            actionField: {
              id: payload.orderUid,
              revenue: moneyToFloat(payload.total),
              tax: moneyToFloat(payload.tax),
              shipping: moneyToFloat(payload.deliveryFee),
              tips: moneyToFloat(payload.tips),
              discount: moneyToFloat(payload.discount),
              loyalty: moneyToFloat(payload.loyalty),
              serviceCharges: moneyToFloat({
                value: payload.serviceCharges.value + payload.bagFee.value,
                currency: payload.total.currency,
              }),
              coupon: payload.discountCode ?? "",
              fulfillment: payload.orderingMode,
              channel: payload.salesChannel,
            },
            products: payload.items.map((i) => ({
              id: i.id,
              name: i.name,
              quantity: i.quantity,
              price: moneyToFloat(i.price),
              total: moneyToFloat({
                value: i.price.value * i.quantity,
                currency: i.price.currency,
              }),
            })),
          },
        },
        currency: payload.total.currency,
      });
    },
  };

  private config: IConfig;
  private regexCache: Record<string, RegExp> = {};
  private scriptSrc = "https://www.googletagmanager.com/gtm.js";
  private dataLayer: Record<string, unknown>[] = [];

  constructor(config: IConfig) {
    super();
    this.config = config;
  }

  protected onInstall(): void {
    if (!this.config.containerId) {
      throw new Error("No google tag manager containerId defined");
    }

    if (!this.isScriptLoaded(this.config.containerId)) {
      (function (w: Window, d: Document, l: string, i: string) {
        (w as unknown as Record<string, unknown>)[l] =
          (w as unknown as Record<string, unknown>)[l] || [];
        (w as unknown as Record<string, unknown[]>)[l].push({
          "gtm.start": new Date().getTime(),
          event: "gtm.js",
        });

        const f = d.getElementsByTagName("script")[0];
        const j = d.createElement("script");
        const dl = l !== "dataLayer" ? `&l=${l}` : "";
        j.async = true;
        j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
        f.parentNode?.insertBefore(j, f);
      })(window, document, "dataLayer", this.config.containerId);
    }

    // @ts-expect-error dataLayer is a custom property
    const dataLayer = window.dataLayer as [];
    if (!dataLayer || !Array.isArray(dataLayer)) {
      throw new Error("[Module:Analytics] Failed to load Google Tag Manager");
    }

    this.dataLayer = dataLayer;
  }

  private track(event: ProviderEvent): void {
    if (!this.dataLayer || !this.analytics) return;
    this.dataLayer.push(event);
  }

  private enhancePayloadAndTrack(event: ProviderEvent): ProviderEvent {
    if (this.analytics?.store.anonymousId) {
      event.anonymousId = this.analytics.store.anonymousId;
    }

    if (this.analytics?.store.user?.id) {
      event.userId = this.analytics.store.user.id;
    }

    this.track(event);

    return event;
  }

  private isScriptLoaded(containerId: string): boolean {
    let regex = this.regexCache[containerId];

    if (!regex) {
      const scriptSrcEscaped = this.scriptSrc
        .replace(/^https?:\/\//, "")
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      regex = new RegExp(scriptSrcEscaped + ".*[?&]id=" + containerId);
      this.regexCache[containerId] = regex;
    }

    const scripts = document.querySelectorAll("script[src]");
    return !!Object.keys(scripts).filter((key) =>
      // @ts-expect-error Object.keys is not ts friendly =/
      (scripts[key].src || "").match(regex),
    ).length;
  }
}
